import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import gql from "graphql-tag";

import { Background } from "components/Background/Backgound";
import { Button } from "components/Button/Button";
import {
  ControlledConfirmationModal,
  ControlledConfirmationModalProps,
} from "components/Modal/ControlledConfirmationModal";
import { ControlledDeletionConfirmationModal } from "components/Modal/DeletionConfirmationModal";
import { Query } from "components/Query/Query";
import { Secret } from "components/Secret/Secret";
import { Spinner } from "components/Spinner/Spinner";
import { Table } from "components/Table/Table";
import {
  DeleteWebhook,
  GetWebhooks,
  GetWebhookSettings,
  RotateWebhookSecret,
  WebhookEventType,
} from "generated/copilot-api-developer";
import { useMutation } from "graphql-client/useMutation";
import { useQuery } from "graphql-client/useQuery";
import { useTranslation } from "i18n";
import { notifier } from "utils/notifier";

import { webhookEventTypeString } from "../WebhookEvents/types";
import { PayloadTestModal } from "./PayloadTestModal";
import { WebhookComposer } from "./WebhookComposer";

gql`
  # schema = COPILOT_API_DEVELOPER

  query GetWebhookSettings {
    me {
      developer {
        organization {
          alertEmails
        }
      }
    }
  }

  fragment Webhook on Webhook {
    uuid
    url
    enabledTypes
    signatureSecret
    createdAt
    rotatedSignatureSecrets {
      signatureSecret
      rotatedAt
    }
  }

  query GetWebhooks {
    webhooks {
      ...Webhook
    }
  }

  mutation DeleteWebhook($uuid: UUID!) {
    deleteWebhook(uuid: $uuid)
  }

  mutation RotateWebhookSecret($uuid: UUID!) {
    rotateWebhookSecret(webhookUuid: $uuid) {
      webhook {
        ...Webhook
      }
    }
  }
`;

type ComposerState =
  | { mode: "create" }
  | { mode: "edit"; webhookUuid: UUID }
  | { mode: "test"; webhookUuid: UUID };

type ConfirmationModalState =
  | Omit<ControlledConfirmationModalProps, "onHide" | "cta" | "children">
  | undefined;

type PayloadTestModalState = { webhookUuid: UUID };

export const Webhooks = () => {
  const t = useTranslation();
  const { data, loading } = useQuery(GetWebhooks);
  const [deleteWebhook] = useMutation(DeleteWebhook);
  const [confirmationModalState, setConfirmationModalState] =
    useState<ConfirmationModalState>(undefined);
  const [composerState, setComposerState] = useState<ComposerState>();
  const [payloadTestModalState, setPayloadTestModalState] =
    useState<PayloadTestModalState>();
  const [rotateConfirmationModalState, setRotateConfirmationModalState] =
    useState<ConfirmationModalState>(undefined);
  const [rotateWebhookSecret] = useMutation(RotateWebhookSecret);

  return (
    <Background className="flex-col flex-fill overflow-auto p-16 lg:p-44 space-y-24">
      {composerState && (
        <WebhookComposer
          onClose={() => setComposerState(undefined)}
          webhook={
            composerState.mode === "edit"
              ? data?.find(
                  (webhook) => webhook.uuid === composerState.webhookUuid,
                )
              : undefined
          }
        />
      )}

      {confirmationModalState && (
        <ControlledDeletionConfirmationModal
          title={t("developers.webhooks.webhooks.delete_webhook")}
          suffix={t("developers.webhooks.webhooks.to_delete_this_webhook")}
          {...confirmationModalState}
          onHide={() => setConfirmationModalState(undefined)}
        />
      )}

      {payloadTestModalState && (
        <PayloadTestModal
          onHide={() => setPayloadTestModalState(undefined)}
          {...payloadTestModalState}
        />
      )}

      {rotateConfirmationModalState && (
        <ControlledConfirmationModal
          title={t("developers.webhooks.webhooks.rotate_webhook_secret")}
          onHide={() => setRotateConfirmationModalState(undefined)}
          cta={{
            label: t("developers.webhooks.webhooks.rotate"),
          }}
          {...rotateConfirmationModalState}
        >
          {t("developers.webhooks.webhooks.to_rotate_this_webhook_secret")}
        </ControlledConfirmationModal>
      )}

      <div className="flex-col">
        <h1 className="text-primary-dark text-24 font-bold">
          {t("developers.webhooks.webhooks.webhooks")}
        </h1>
        <div className="mt-8">
          Check{" "}
          <a
            className="link"
            href="https://docs.nabla.com/webhook/setup-webhooks"
            target="_blank"
            rel="noreferrer"
          >
            Webhooks Setup
          </a>{" "}
          guide for details on how to set up webhooks integration with the Nabla
          Core API.
        </div>
        <Query query={GetWebhookSettings}>
          {(webhookSettings) => (
            <div className="mt-8">
              {t(
                "webhooks.if_any_of_your_webhooks_fail_you_will_be_notified_at",
              )}{" "}
              {webhookSettings.developer.organization.alertEmails}
            </div>
          )}
        </Query>
      </div>
      <div className="flex items-center">
        <div className="flex-col flex-1">
          {data && (
            <span>
              {data.length} {t("developers.webhooks.webhooks.webhooks")}
            </span>
          )}
        </div>
        <Button
          label={t("developers.webhooks.webhooks.add_webhook")}
          onClick={() => setComposerState({ mode: "create" })}
        />
      </div>
      {loading ? (
        <Spinner />
      ) : data ? (
        <Table
          elements={data}
          fieldHeaders={[
            t("developers.webhooks.webhooks.url"),
            t("developers.webhooks.webhooks.enabled_typed"),
            t("developers.webhooks.webhooks.secret"),
          ]}
          fields={(webhook) => [
            webhook.url,
            <div
              key={`enabledTypes_${webhook.uuid}`}
              className="flex flex-col gap-4 items-start"
            >
              {webhook.enabledTypes.map((type: WebhookEventType) => (
                <samp
                  key={type}
                  className="bg-grey-100 border border-grey-200 py-0 px-4 inline-block overflow-hidden text-12 w-fit"
                >
                  {webhookEventTypeString(type)}
                </samp>
              ))}
            </div>,
            <div
              key={`secret_${webhook.uuid}`}
              className="flex flex-col gap-4 items-start"
            >
              <Secret secret={webhook.signatureSecret} />
              {webhook.rotatedSignatureSecrets &&
                webhook.rotatedSignatureSecrets.length > 0 && (
                  <span className="text-12 text-grey-500">
                    {t("developers.webhooks.webhooks.rotated", {
                      time: formatDistanceToNow(webhook.createdAt.getDate(), {
                        addSuffix: true,
                      }),
                    })}
                  </span>
                )}
            </div>,
          ]}
          menuItems={(webhook) => [
            {
              icon: "edit",
              text: t("developers.webhooks.webhooks.edit"),
              onClick: (close: () => void) => {
                close();
                setComposerState({ mode: "edit", webhookUuid: webhook.uuid });
              },
            },
            {
              icon: "bug",
              text: t("developers.webhooks.webhooks.send_a_test_payload"),
              onClick: (close: () => void) => {
                close();
                setPayloadTestModalState({ webhookUuid: webhook.uuid });
              },
            },
            {
              icon: "refresh",
              text: t("developers.webhooks.webhooks.rotate_secret"),
              onClick: (close: () => void) => {
                close();
                setRotateConfirmationModalState({
                  onConfirm: () =>
                    rotateWebhookSecret(
                      { uuid: webhook.uuid },
                      {
                        onSuccess: (_) => {
                          notifier.success(
                            t(
                              "developers.webhooks.webhooks.webhook_secret_rotated",
                            ),
                          );
                          setRotateConfirmationModalState(undefined);
                        },
                        refetchQueries: ["GetWebhooks"],
                      },
                    ),
                });
              },
            },
            {
              icon: "trash",
              text: t("developers.webhooks.webhooks.permanently_delete"),
              className: "text-danger",
              onClick: (close: () => void) => {
                close();
                setConfirmationModalState({
                  onConfirm: () =>
                    deleteWebhook(
                      { uuid: webhook.uuid },
                      {
                        onSuccess: (_, client) => {
                          notifier.success(
                            t("developers.webhooks.webhooks.webhook_deleted"),
                          );
                          setConfirmationModalState(undefined);
                          client.remove("Webhook", webhook.uuid);
                        },
                      },
                    ),
                });
              },
            },
          ]}
        />
      ) : null}
    </Background>
  );
};
